/**
 * Local-currency price helper.
 *
 * Stripe charges the customer in the original currency (AED for /7days &
 * /nabd, USD for /reply). This helper ADDITIONALLY shows a localised price
 * label so visitors from KW/BH/QA/SA see something familiar next to the
 * actual charge amount.
 *
 * The Stripe charge itself is unchanged — only the display label varies.
 * Conversion rates below are static (refreshed manually when needed) since
 * GCC currencies are USD-pegged with negligible daily variance.
 */

// USD-pegged FX → 1 unit of source currency = X units of target.
// Both AED and SAR are pegged at ~3.6725 to USD.
// KWD is the wild card — not pegged, hovering around 0.305 KWD per USD.
const FX = {
  AED: {
    KWD: 0.0838,   // 1 AED = 0.0838 KWD
    BHD: 0.1027,   // 1 AED = 0.1027 BHD
    QAR: 0.9907,   // 1 AED = 0.99 QAR (nearly 1:1)
    SAR: 1.020,    // 1 AED = 1.02 SAR
    USD: 0.272,    // 1 AED = 0.272 USD
  },
  USD: {
    KWD: 0.308,
    BHD: 0.377,
    QAR: 3.64,
    SAR: 3.75,
    AED: 3.6725,
  },
};

const SYMBOL = {
  AED: "AED",
  USD: "USD",
  KWD: "KWD",   // Kuwaiti dinar
  BHD: "BHD",   // Bahraini dinar
  QAR: "QAR",   // Qatari riyal
  SAR: "SAR",   // Saudi riyal
};

// Map ISO country code → preferred display currency for visitors there.
// We keep UAE on AED (their native currency) and Saudi on SAR (familiar).
// Reply Rescue (global) ignores this and stays in USD.
const COUNTRY_PREF = {
  KW: "KWD",
  BH: "BHD",
  QA: "QAR",
  SA: "SAR",
  AE: "AED",
};

/**
 * Returns a localised label like "≈ 12 KWD" if the visitor's country prefers
 * a different currency than the base. Returns null if the base matches the
 * visitor's preference (so we don't show "≈ 199 AED" for UAE visitors).
 *
 * @param amount       Numeric amount in the base currency
 * @param baseCurrency "AED" | "USD"
 * @param country      Visitor's ISO country code from /api/geo
 */
export const localisedPrice = (amount, baseCurrency, country) => {
  if (!country || !amount) return null;
  const target = COUNTRY_PREF[country.toUpperCase()];
  if (!target || target === baseCurrency) return null;
  const rate = (FX[baseCurrency] || {})[target];
  if (!rate) return null;
  const converted = amount * rate;
  // Round KWD/BHD to 1 decimal (they're high-value units, fractional matters)
  // SAR/QAR/USD/AED round to whole numbers (cleaner display).
  const display = (target === "KWD" || target === "BHD")
    ? converted.toFixed(1)
    : Math.round(converted).toString();
  return `≈ ${display} ${SYMBOL[target] || target}`;
};

export default localisedPrice;
